import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/feature/Seo";
import PageBreadcrumb from "@/components/feature/PageBreadcrumb";
import SubscribeForm from "@/components/feature/SubscribeForm";

const values = [
  {
    icon: "ri-flashlight-line",
    title: "追更优先",
    desc: "以「连载更新与追更提醒」为核心，把最新动态第一时间送达读者，不遗漏任何一话。",
  },
  {
    icon: "ri-focus-2-line",
    title: "内容聚焦",
    desc: "精选值得追的连载漫画与系列作品，用清晰的分类与目录，让发现好作品变得简单。",
  },
  {
    icon: "ri-hand-heart-line",
    title: "尊重创作",
    desc: "重视版权与原创，尊重每一位作者与读者的付出，共建健康友好的漫画社区氛围。",
  },
  {
    icon: "ri-global-line",
    title: "开放共享",
    desc: "面向所有漫画爱好者免费开放追更服务，追求简单、顺畅、高效的阅读体验。",
  },
];

const testimonials = [
  {
    name: "追更星人",
    role: "热血漫爱好者",
    text: "自从用了漫蛙的追更提醒，再也没错过《火焰风云》的新章，太省心了。",
  },
  {
    name: "糖分补给站",
    role: "治愈漫爱好者",
    text: "分类很清楚，《四季小憩》这类治愈作品一上新就能立刻看到，体验很顺畅。",
  },
  {
    name: "夜航员",
    role: "悬疑漫爱好者",
    text: "作者专区很贴心，程野老师的系列作品都能在同一个地方追完，强烈推荐。",
  },
];

export default function About() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "/" },
          { "@type": "ListItem", position: 2, name: "关于我们" },
        ],
      },
      {
        "@type": "AboutPage",
        name: "关于漫蛙漫画网",
        description: "漫蛙漫画网是一个专注连载更新与追更提醒的中文漫画阅读平台。",
        inLanguage: "zh-CN",
      },
    ],
  };

  return (
    <Layout>
      <Seo
        title="关于漫蛙漫画网 | 品牌介绍与发展理念"
        description="了解漫蛙漫画网的品牌介绍、网站定位、发展理念与内容原则。我们专注连载更新与追更提醒，致力于为每一位漫画爱好者提供顺畅高效的追更体验。"
        keywords="漫蛙漫画网介绍,追更平台,漫画阅读,发展理念"
        jsonLd={jsonLd}
      />

      <div className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 pb-8 pt-24 md:px-6 md:pt-28">
          <PageBreadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-4 font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">
            关于漫蛙漫画网
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-foreground-500">
            一个为「追更」而生的中文漫画阅读平台。
          </p>
        </div>
      </div>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
            <div>
              <h2 className="font-heading text-2xl font-extrabold text-foreground-950">品牌介绍</h2>
              <p className="mt-4 text-base leading-relaxed text-foreground-700">
                漫蛙漫画网专注连载更新与追更提醒，收录最新章节、作品列表、作者专区及系列漫画。我们把「第一时间获知更新动态」作为最重要的承诺，帮助每一位追更党不再错过任何一话。
              </p>
              <p className="mt-4 text-base leading-relaxed text-foreground-700">
                从热血战斗到温馨治愈，从科幻冒险到悬疑推理，漫蛙漫画网用清晰的分类与顺畅的阅读路径，让好作品更容易被发现，让追更体验更高效。
              </p>
            </div>
            <div>
              <h2 className="font-heading text-2xl font-extrabold text-foreground-950">网站定位</h2>
              <p className="mt-4 text-base leading-relaxed text-foreground-700">
                我们不是内容最全的「大全站」，而是以读者体验为先的「追更工具型平台」。围绕连载更新、追更提醒、作品列表与作者专区四大板块，构建一个简单、顺畅、值得信赖的漫画阅读空间。
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {["连载更新", "追更提醒", "最新章节", "作品列表", "作者专区", "系列漫画"].map((k) => (
                  <span key={k} className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                    {k}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <h2 className="font-heading text-2xl font-extrabold text-foreground-950">发展理念与内容原则</h2>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v) => (
              <article key={v.title} className="rounded-xl bg-background-50 p-6 ring-1 ring-background-200">
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-500 text-xl text-background-50">
                  <i className={v.icon} aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <h2 className="font-heading text-2xl font-extrabold text-foreground-950">读者口碑</h2>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((t) => (
              <figure key={t.name} className="rounded-xl bg-background-100 p-6 ring-1 ring-background-200">
                <div className="flex gap-1 text-accent-500">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill" aria-hidden="true" />
                  ))}
                </div>
                <blockquote className="mt-4 text-sm leading-relaxed text-foreground-700">「{t.text}」</blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 font-bold text-primary-700">
                    {t.name.charAt(0)}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section id="subscribe" className="scroll-mt-16 bg-gradient-to-br from-primary-700 via-primary-800 to-secondary-900 py-16 md:py-20">
        <div className="mx-auto max-w-4xl px-4 text-center md:px-6">
          <h2 className="font-heading text-2xl font-extrabold text-background-50 md:text-3xl">
            加入漫蛙，开启顺畅的追更体验
          </h2>
          <p className="mt-3 text-sm text-background-100/85">
            订阅追更提醒，最新章节第一时间送达。免费、快速、随时可退订。
          </p>
          <div className="mt-8">
            <SubscribeForm />
          </div>
          <div className="mt-10 flex flex-col items-center justify-center gap-2 text-sm text-background-100/85 sm:flex-row sm:gap-6">
            <span className="flex items-center gap-2">
              <i className="ri-mail-line text-accent-400" aria-hidden="true" />
              contact@manwa.example.com
            </span>
            <span className="flex items-center gap-2">
              <i className="ri-global-line text-accent-400" aria-hidden="true" />
              <Link to="/works" className="hover:text-accent-300">浏览全部作品</Link>
            </span>
          </div>
        </div>
      </section>
    </Layout>
  );
}